<template>
    <div class="fx-location-component" ref="locationComponent">
        <div class="selection" @click="openMenu">
            <span class="selection-value" v-if="selectedLocation">
                <slot name="location" :location="selectedLocation">
                    <!-- This content will get replaced by content passed to the 'location' slot -->
                    {{ selectedLocation.city }},&nbsp;{{ selectedLocation.subdivision }},&nbsp;{{
                        selectedLocation.country_code
                    }}
                </slot>
            </span>
            <span class="selection-placeholder" v-if="!selectedLocation">
                {{ placeholder }}
            </span>
            <span class="open-menu-icon" v-show="!selectedLocation">
                <slot name="caret">
                    <span class="icon-caret-down"></span>
                </slot>
            </span>
        </div>
        <span class="cancel-selection icon-cancel" v-show="selectedLocation" @click="select(null)"></span>
        <div class="location-overlay" v-show="isMenuOpen" @click="closeMenu"></div>
        <div class="location-menu" v-show="isMenuOpen">
            <input
                class="location-input"
                type="text"
                ref="locationInput"
                :placeholder="placeholder"
                aria-label="Location"
                v-model="query"
                @input="onInput"
                @keyup.down="onArrowDown"
                @keyup.up="onArrowUp"
                @keyup.enter="onEnter"
                @keyup.esc="closeMenu"
            />
            <ul class="fx-list location-options">
                <li
                    v-if="currentLocation"
                    class="current-location-option"
                    :class="{ highlighted: highlightedSuggestionIndex == 0 }"
                    @click="
                        select(currentLocation, true)
                        closeMenu()
                    "
                >
                    <span class="crosshair-icon"></span>
                    {{ currentLocationLabel }}
                </li>
                <li
                    v-for="(location, index) in locations"
                    :class="{
                        highlighted: highlightedSuggestionIndex == (currentLocation ? index + 1 : index)
                    }"
                    @click="
                        select(location)
                        closeMenu()
                    "
                >
                    <slot name="location" :location="location">
                        <!-- This content will get replaced by content passed to the 'location' slot -->
                        {{ location.city }},&nbsp;{{ location.subdivision }},&nbsp;{{ location.country_code }}
                    </slot>
                </li>
            </ul>
            <span class="close-menu icon-close" @click="closeMenu"></span>
        </div>
    </div>
</template>

<script>
import LocationUtil from '../utilities/LocationUtil'

export default {
    props: {
        currentLocation: { type: Object },
        selectedLocation: { type: Object },
        placeholder: { type: String },
        currentLocationLabel: { type: String }
    },
    data () {
        return {
            query: '',
            locations: null,
            isMenuOpen: false,
            suggestionElements: null,
            highlightedSuggestionIndex: null
        }
    },
    methods: {
        select (location) {
            if (LocationUtil.areLocationsEqual(location, this.selectedLocation)) {
                return
            }
            this.$emit('select', location)
        },
        openMenu () {
            this.isMenuOpen = true
            // https://stackoverflow.com/questions/33093490/focus-doesnt-work-in-input-on-chrome
            setTimeout(() => {
                this.$refs.locationInput.focus()
            }, 0)
        },
        closeMenu () {
            this.isMenuOpen = false
            this.query = ''
            this.locations = null
            this.resetHighlight()
        },
        resetHighlight () {
            this.highlightedSuggestionIndex = null
        },
        fetchLocationSuggestions () {
            return this.$marketplace.searchLocations(this.query).then(
                (response) => {
                    this.locations = response
                    return Promise.resolve(response)
                },
                (errors) => {
                    this.$emit('error', errors)
                    return Promise.reject(errors)
                }
            )
        },
        getSuggestionElements () {
            const { locationComponent } = this.$refs
            return locationComponent ? locationComponent.querySelectorAll('.location-options>li') : []
        },
        hasCoordinateChanged (coord1, coord2) {
            if (coord1 && coord2) {
                return coord1[0] !== coord2[0] || coord1[1] !== coord2[1]
            }
            return coord1 !== coord2
        },
        onInput () {
            this.resetHighlight()
            if (this.query.length >= 3) {
                this.fetchLocationSuggestions()
            } else {
                this.locations = null
            }
        },
        onEnter () {
            if (this.highlightedSuggestionIndex === null) return
            let selectedLocation = null
            if (this.currentLocation) {
                if (this.highlightedSuggestionIndex === 0) {
                    selectedLocation = this.currentLocation
                } else {
                    selectedLocation = this.locations[this.highlightedSuggestionIndex - 1]
                }
            } else {
                selectedLocation = this.locations[this.highlightedSuggestionIndex]
            }
            this.select(selectedLocation)
            this.closeMenu()
        },
        onArrowDown () {
            if (this.highlightedSuggestionIndex === null && this.suggestionElements.length) {
                this.highlightedSuggestionIndex = 0
            } else if (this.highlightedSuggestionIndex < this.suggestionElements.length - 1) {
                this.highlightedSuggestionIndex += 1
            }
        },
        onArrowUp () {
            if (this.highlightedSuggestionIndex === 0) {
                this.highlightedSuggestionIndex = null
            } else if (this.highlightedSuggestionIndex) {
                this.highlightedSuggestionIndex -= 1
            }
        }
    },
    beforeCreate () {
        if (!this.$marketplace) {
            throw new Error('This module requires the Marketplace plugin to be installed')
        }
    },
    updated () {
        this.suggestionElements = this.getSuggestionElements()
    }
}
</script>

<style lang="postcss" scoped>
@import 'theme';

.fx-location-component {
    position: relative;
    display: inline-flex;
    align-items: center;
}

.selection {
    display: flex;
    align-items: center;
    cursor: pointer;
}

.selection-value {
    font-weight: 600;
    text-decoration: underline;
}

.selection-placeholder {
    font-weight: 400;
}

.open-menu-icon {
    cursor: pointer;
    font-size: var(--fs-smaller);
    margin-left: 4px;
}

.cancel-selection {
    display: flex;
    align-items: center;
    margin-left: 6px;
    cursor: pointer;
}

.location-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}

.location-input {
    height: 28px;
    font-size: var(--fs-normal);
    padding: 8px;
    outline: none;
    border: none;
}

.location-menu {
    position: absolute;
    top: -6px;
    left: -1px;
    min-width: 100%;
    background-color: white;
    box-shadow: var(--sh-deep);
    border-radius: var(--d-border-radius);
    overflow: hidden;
}

.location-options {
    & > li {
        cursor: pointer;
        padding: 8px;
        border-top: solid 1px var(--c-gray-8);
        white-space: nowrap;
        &:hover,
        &.highlighted {
            color: var(--c-tertiary);
            background-color: var(--c-interact-hover);
        }
    }
}

.current-location-option {
    display: flex;
    align-items: center;
}

.current-location-icon {
    display: inline-flex;
    margin-right: 6px;
}

.close-menu {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    width: 28px;
    height: 28px;
}
</style>
